import React, { useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../context/UserContext";
import CheckCircleSolid from "../../assets/images/check-circle-solid.svg";
import CrossCircleSolid from "../../assets/images/cross-circle-solid.svg";
import html2pdf from "html2pdf.js";
import AiDisclosure from "../../components/AffordableOption/AiDisclosure";
import FormattedDescription from "../../components/AffordableOption/FormattedDescription";
import DisclosureButtons from "../../components/AffordableOption/DisclosureButtons";

function Disclosure({
  chosenComponents,
  components,
  handleRestart,
  mileage,
  products,
  selectedProgram,
  vehicle,
}) {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const userToken = user?.usertoken;
  const componentsShown = [];
  const contentRef = useRef(null);

  // Early return if user is not logged in
  useEffect(() => {
    if (!userToken) {
      navigate("/", { replace: true });
      return;
    }
    if (!vehicle?.vin) {
      navigate("/vehicle-submission", { replace: true });
      return;
    }
  }, [userToken, vehicle, navigate]);

  // If vehicle is not loaded yet, don't render anything
  if (!vehicle?.vin) {
    return null;
  }

  const pdfOptions = {
    margin: 0.5,
    filename: `Affordable-Option-Disclosure-${vehicle.vin}.pdf`,
    image: { type: "jpeg", quality: 0.98 },
    html2canvas: { scale: 2 },
    jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
    pagebreak: { mode: ["css"], avoid: ["li"] },
  };
  const expirationMileage = selectedProgram?.term_mileage
    ? parseInt(selectedProgram.term_mileage) + parseInt(mileage)
    : mileage;
  const currentDate = new Date();
  const currentDateFormatted = currentDate.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  const numMonths = Number(selectedProgram?.term);
  const expirationDate = new Date();
  expirationDate.setMonth(expirationDate.getMonth() + numMonths);
  const expirationDateFormatted = expirationDate.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const handleDownloadPdf = () => {
    const content = contentRef.current;

    html2pdf().set(pdfOptions).from(content).save();
  };

  const getProductName = (currentProduct) => {
    return currentProduct.name.replace(/Component : /g, "");
  };

  const getProductDescription = (productName) => {
    const matchedSubtype = products[0].subtypes.find(
      (subtype) => subtype.name === productName
    );
    return matchedSubtype?.coverage_description !== null
      ? matchedSubtype.coverage_description
      : matchedSubtype.description;
  };

  const coveredComponents = selectedProgram?.products.filter(
    (component) =>
      component.required === true || chosenComponents.includes(component.id)
  );
  const nonCoveredComponents = selectedProgram?.products.filter((component) => {
    const componentName = getProductName(component);
    if (components.includes(componentName)) {
      componentsShown.push(componentName);
    }

    return (
      component.required === false && !chosenComponents.includes(component.id)
    );
  });
  const componentsMissing = components.filter(
    (component) => !componentsShown.includes(component)
  );

  const handleBack = () => {
    navigate("/recommendations", { replace: true });
  };

  const handleRestartProcess = () => {
    handleRestart();
    navigate("/vehicle-submission", { replace: true });
  };

  const handleCreateContract = () => {
    navigate("/contract-create", { replace: true });
  };

  return (
    <div className="disclosure-page" ref={contentRef}>
      <div className="container">
        <div className="row pt-4">
          <div className="col-12 text-end d-print-none" data-html2canvas-ignore>
            <DisclosureButtons
              onDownload={handleDownloadPdf}
              onBack={handleBack}
              onRestart={handleRestartProcess}
              onCreateContract={handleCreateContract}
            />
          </div>
        </div>
        <div className="row pt-2">
          <div className="col-12">
            <h2>Affordable Option Disclosure</h2>
            <p>
              <strong>Date:</strong> {currentDateFormatted}
            </p>
            {selectedProgram === null ? (
              <p>
                For your {vehicle.year} {vehicle.make} {vehicle.model} with{" "}
                {mileage} miles, you have not selected an Affordable Option
                program.
              </p>
            ) : (
              <p>
                For your {vehicle.year} {vehicle.make} {vehicle.model} (VIN:{" "}
                {vehicle.vin}) with {mileage} miles, you have chosen the{" "}
                {selectedProgram.name} plan for {selectedProgram.term} months
                with a deductible of ${selectedProgram.deductible}, expiration
                mileage of {expirationMileage}, and expiration date of{" "}
                {expirationDateFormatted}.
              </p>
            )}
          </div>
        </div>
        {selectedProgram !== null ? (
          <div className="row">
            <div className="col-6">
              <h5>
                <strong>
                  <img
                    className="me-2"
                    src={CheckCircleSolid}
                    alt="Covered Components"
                    style={{ width: "20px" }}
                    data-html2canvas-ignore
                  />
                  Components covered by your Affordable Option plan:
                </strong>
              </h5>
              <ul>
                {coveredComponents.map((component) => (
                  <li key={component.id}>
                    <strong>{getProductName(component)}</strong>
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-6">
              <h5>
                <strong>
                  <img
                    className="me-2"
                    src={CrossCircleSolid}
                    alt="Non-Covered Components"
                    style={{ width: "20px" }}
                    data-html2canvas-ignore
                  />
                  Components not covered by your Affordable Option plan:
                </strong>
              </h5>
              {nonCoveredComponents.length > 0 ||
              componentsMissing.length > 0 ? (
                <ul>
                  {nonCoveredComponents.map((component) => (
                    <li key={component.id}>
                      <strong>{getProductName(component)}</strong>
                    </li>
                  ))}
                  {componentsMissing.map((component) => (
                    <li key={component}>
                      <strong>{component}</strong>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>
                  Your selected Affordable Option plan covers all component
                  areas.
                </p>
              )}
            </div>
            <div className="col-12">
              <h5 className="fw-bold">Covered Components</h5>
              {coveredComponents.map((component) => (
                <div key={component.id}>
                  <h6>
                    <strong>{getProductName(component)}</strong>
                  </h6>
                  <FormattedDescription
                    description={getProductDescription(
                      getProductName(component)
                    )}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : null}
        <div className="row">
          <div className="col-12">
            <AiDisclosure />
          </div>
          <div className="col-12">
            <p className="fw-bold text-uppercase">
              See your contract for full disclosure of covered and non-covered
              components.
            </p>
          </div>
        </div>
        <div className="row">
          <div className="col-6">
            <hr className="mt-5" />
            Buyer Signature
            <hr className="mt-5" />
            Date
          </div>
          <div className="col-6">
            <hr className="mt-5" />
            Dealer Signature
            <hr className="mt-5" />
            Date
          </div>
        </div>
        <div className="row my-4 d-print-none" data-html2canvas-ignore>
          <div className="col-12">
            <DisclosureButtons
              onDownload={handleDownloadPdf}
              onBack={handleBack}
              onRestart={handleRestartProcess}
              onCreateContract={handleCreateContract}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Disclosure;
